import { useSearchParams } from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import styles from './styles.module.scss';
import { ErrorCard } from '@starsoft/common/components';
import { useWithdrawalById } from '../../../../../../../api';
import TransactionResult from '../../../../../Result';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

/**
 * WithdrawResponseContent Component
 * This component is responsible for rendering the response content of a withdrawal transaction.
 * It fetches the withdrawal details using the withdrawal ID from the URL search parameters.
 *
 * @returns {JSX.Element} A skeleton loader, an error card, or the transaction result component.
 *
 * @example
 * // If the withdrawal is loading, it returns:
 * <div className={styles.skeleton} />
 * // If there is an error fetching the withdrawal, it returns:
 * <ErrorCard refetch={refetch} error={error} />
 * // If the withdrawal is successfully fetched, it returns:
 * <TransactionResult transaction={withdraw} />
 */
export default function WithdrawResponseContent() {
  // Retrieve the URL search parameters
  const search: URLSearchParams = useSearchParams();
  // Extract the 'withdraw-id' parameter from the search parameters
  const id: Nullable<string> = search.get('withdraw-id');
  // Fetch the withdrawal details using the 'withdraw-id'
  const { error, refetch, withdraw, isLoading } = useWithdrawalById({
    id: id ? (parseToBigInt(id) as bigint) : undefined,
  });

  // If the withdrawal details are still loading, render a skeleton loader
  if (isLoading) {
    return <div className={styles.skeleton} />;
  }

  // If there is an error fetching the withdrawal details, render an error card
  if (error) {
    return <ErrorCard refetch={refetch} error={error} />;
  }

  // If the withdrawal details are successfully fetched, render the transaction result
  return <TransactionResult transaction={withdraw} />;
}
